import { has, isNull, min } from 'lodash/fp';
import { Day, TimeUnit } from '@mews/enums';
import { DateTime, DateTimePart, DateTimeService, toDateTimeLuxon } from '@mews/framework';
import { EnumValue } from '@mews/enumify';
import { alpha, getToken } from '@optimus-web/core';
import { DENSE_LIGHT_THEME } from '@optimus-web/theme';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { isEqualMonthAndYear } from '@mews-commander/utils';
import { getParentUnit, getTimeUnitIndexDate, getTimeUnitScale, timeUnitDateTimeOptimization } from './utils/timeUnit';

const MINUTE = 60 * 1000;

interface TimelineCache {
    hourDateTime: DateTime | null;
    todayDateTime: DateTime | null;
    monthDateTime: DateTime | null;
    dayNameCache: {};
    dateStringCache: {};
    dayDateCache: {};
    timeUnitDateCache: {};
    dayDateTimeCache: {};
    dayColorCache: {};
    dateTimeFractionCache: {};
    timeUnitDateTimeCache: {};
    timeUnitParentCache: {};
    dateTimeFractionTimeUnitCache: {};
    isoDayCache: {};
    dayDiffCache: {};
    textsCache: {};
    nowCache: DateTime | null;
    nowCacheTimestamp: number;
    utcToTimeUnitIndexCache: {};
    indexDateTimeCache: {};
}

let caches: TimelineCache = getEmptyCaches();

export const TimelineService = {
    localizeTimeUnitDate(this: void, dayIndex: number, timeUnit: TimeUnit) {
        const container = getContainer();
        const { appLocalizationComponent } = container();
        return fromCache(caches.timeUnitDateCache, `${timeUnit}${dayIndex}`, () =>
            appLocalizationComponent.localizeDate(indexToTimeUnitDateTime(dayIndex, timeUnit))
        );
    },
    indexWithTimeUnitToColor(this: void, dayIndex: number, timeUnit = TimeUnit.Day) {
        return fromCache(caches.dayColorCache, `${timeUnit}${dayIndex}`, () => {
            switch (timeUnit) {
                case TimeUnit.Day:
                    if (isToday(dayIndex)) {
                        return getToken('paletteBrandIndigo50')({ theme: DENSE_LIGHT_THEME });
                    }
                    return getDayOrWeekendColor(dayIndex);
                case TimeUnit.Hour:
                    if (isToday(dayIndex)) {
                        return getToken('paletteBrandIndigo50')({ theme: DENSE_LIGHT_THEME });
                    }
                    return getToken('paletteBrandNight0')({ theme: DENSE_LIGHT_THEME });
                case TimeUnit.Month:
                    if (isEqualMonthAndYear(indexToTimeUnitDateTime(dayIndex, timeUnit), getTodayDateTime())) {
                        return getToken('paletteBrandIndigo50')({ theme: DENSE_LIGHT_THEME });
                    }
                    return getToken('paletteBrandNight0')({ theme: DENSE_LIGHT_THEME });
                default:
                    throw Error('Time unit not provided');
            }
        });
    },
    indexToTimeUnitDateTime,
    indexToTimeUnitParentUnit,
    getCurrentTimeUnitDateTime,
    utcToTimeUnitIndex(this: void, utcString: string, timezone: string, timeUnit: TimeUnit): number {
        // Drop minutes in string to speedup caching, with 150px day width 10 minutes are below 1px resolution.
        const utc = timeUnitDateTimeOptimization(utcString, timeUnit);
        return fromCache(caches.utcToTimeUnitIndexCache, `${utc}${timeUnit}`, () =>
            dateTimeToTimeUnitIndex(parseUtc(utc, timezone), timeUnit)
        );
    },
    fromTextCache(this: void, text: string, width: number, getter: () => { text: string; width: number }) {
        const key = `${text}${width}`;
        return fromCache(caches.textsCache, key, getter);
    },
    clearCaches(this: void) {
        caches = getEmptyCaches();
    },
};

function isToday(day: number) {
    const container = getContainer();
    const { appClockComponent, appDateComponent } = container();
    const now = appClockComponent.getValue();
    if (caches.nowCacheTimestamp + MINUTE < now) {
        caches.nowCache = appDateComponent.now();
        caches.nowCacheTimestamp = now;

        if (caches.nowCache === null) {
            throw new TypeError('Expected nowCache to be DateTime');
        }

        return fromCache(
            caches.dayDiffCache,
            day,
            () =>
                DateTimeService.diffDays(
                    caches.nowCache as DateTime,
                    DateTimeService.addDays(caches.nowCache as DateTime, day)
                ) === 0,
            true
        );
    }

    if (caches.nowCache === null) {
        throw new TypeError('Expected nowCache to be DateTime');
    }

    return fromCache(
        caches.dayDiffCache,
        day,
        () =>
            DateTimeService.diffDays(
                caches.nowCache as DateTime,
                DateTimeService.addDays(caches.nowCache as DateTime, day)
            ) === 0
    );
}

function isDay(dayIndex: number, isoDay: EnumValue<keyof typeof Day, number>) {
    const dayCache = fromCache(caches.isoDayCache, dayIndex, () => ({}));
    return fromCache(dayCache, isoDay.value, () => {
        const dateTime = indexToDateTime(dayIndex);
        return DateTimeService.getDay(dateTime) === isoDay.value;
    });
}

function getDayOrWeekendColor(dayIndex: number) {
    if (isDay(dayIndex, Day.Saturday) || isDay(dayIndex, Day.Sunday)) {
        return alpha(
            getToken('paletteBrandNight50')({ theme: DENSE_LIGHT_THEME }),
            getToken('opacity800')({ theme: DENSE_LIGHT_THEME })
        );
    }

    return getToken('paletteBrandNight0')({ theme: DENSE_LIGHT_THEME });
}

function parseUtc(utcString: string, timezone: string) {
    return fromCache(caches.dateStringCache, utcString, () => DateTimeService.fromUtcString(utcString, timezone));
}

function calculateMonthIndex(dateTime: DateTime, timeUnit: TimeUnit) {
    const normalizedStart = DateTimeService.startOfMonth(dateTime);
    const startOfIndex = DateTimeService.diffMonths(normalizedStart, getCurrentTimeUnitDateTime(timeUnit));
    return (
        startOfIndex +
        (min([
            DateTimeService.diffHours(dateTime, normalizedStart) / getTimeUnitScale(timeUnit, dateTime),
            getTimeUnitScale(timeUnit, dateTime),
        ]) as number)
    );
}

const diffHoursWithoutRounding = DateTimeService.diff(DateTimePart.Hour, { roundDown: false });

function dateTimeToTimeUnitIndex(dateTime: DateTime, timeUnit: TimeUnit) {
    return fromCache(caches.dateTimeFractionTimeUnitCache, `${dateTime.value}${timeUnit}`, () => {
        if (timeUnit === TimeUnit.Month) {
            return calculateMonthIndex(dateTime, timeUnit);
        }

        const currentDateTime = getCurrentTimeUnitDateTime(timeUnit);
        const currentDateTimeLuxon = toDateTimeLuxon(currentDateTime);
        const luxonDateTime = toDateTimeLuxon(dateTime);

        // Offset adjustment for DST change
        const offsetDiff = (luxonDateTime.offset - currentDateTimeLuxon.offset) / 60;

        return (
            (diffHoursWithoutRounding(dateTime, getCurrentTimeUnitDateTime(timeUnit)) + offsetDiff) /
            getTimeUnitScale(timeUnit, dateTime)
        );
    });
}

function indexToDateTime(dayIndex: number) {
    return fromCache(caches.dayDateTimeCache, dayIndex, () => DateTimeService.addDays(getTodayDateTime(), dayIndex));
}

function indexToTimeUnitDateTime(index: number, timeUnit: TimeUnit): DateTime {
    return fromCache(caches.timeUnitDateTimeCache, `${timeUnit}${index}`, () => {
        const currentDateTime = getCurrentTimeUnitDateTime(timeUnit);
        return getTimeUnitIndexDate(currentDateTime, index, timeUnit);
    });
}

function indexToTimeUnitParentUnit(index: number, timeUnit: TimeUnit) {
    return fromCache(caches.timeUnitParentCache, `${timeUnit}${index}`, () => {
        const currentDateTime = getCurrentTimeUnitDateTime(timeUnit);
        const timeUnitIndexDateTime = getTimeUnitIndexDate(currentDateTime, index, timeUnit);
        return getParentUnit(timeUnitIndexDateTime, timeUnit);
    });
}

function fromCache(cache: $TSFixMe, key: $TSFixMe, getter: $TSFixMe, forceUpdate = false) {
    if (has(key, cache) && !forceUpdate) {
        return cache[key];
    }
    const value = getter();
    cache[key] = value;
    return value;
}

function getTodayDateTime() {
    if (isNull(caches.todayDateTime)) {
        const container = getContainer();
        const { appDateComponent } = container();
        caches.todayDateTime = appDateComponent.today();
    }
    return caches.todayDateTime;
}

function getCurrentTimeUnitDateTime(timeUnit: TimeUnit) {
    const container = getContainer();
    const { appDateComponent } = container();

    if (timeUnit === TimeUnit.Month) {
        if (isNull(caches.monthDateTime)) {
            caches.monthDateTime = DateTimeService.startOfMonth(appDateComponent.today());
        }
        return caches.monthDateTime;
    }
    if (timeUnit === TimeUnit.Hour) {
        if (isNull(caches.hourDateTime)) {
            caches.hourDateTime = DateTimeService.startOfHour(appDateComponent.now());
        }

        return caches.hourDateTime;
    }
    return getTodayDateTime();
}

function getEmptyCaches(): TimelineCache {
    return {
        hourDateTime: null,
        todayDateTime: null,
        monthDateTime: null,
        dayNameCache: {},
        dateStringCache: {},
        dayDateCache: {},
        timeUnitDateCache: {},
        dayDateTimeCache: {},
        dayColorCache: {},
        dateTimeFractionCache: {},
        timeUnitDateTimeCache: {},
        timeUnitParentCache: {},
        dateTimeFractionTimeUnitCache: {},
        isoDayCache: {},
        dayDiffCache: {},
        textsCache: {},
        nowCache: null,
        nowCacheTimestamp: 0,
        utcToTimeUnitIndexCache: {},
        indexDateTimeCache: {},
    };
}
